import { useState, useEffect } from 'react';
import { adminAPI, authAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Settings,
    Users,
    Shield,
    Mail,
    Plus,
    Trash2,
    Copy,
    Key,
    Loader2,
    Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { PlayCircle } from 'lucide-react';
import { refreshExplainerCache } from '../components/setup_guide/useSetupGuide';
import { parseUtc } from '../lib/datetime';

const ROLES = [
    { value: 'super_admin', label: 'Super Admin', description: 'Full access to all clients and settings' },
    { value: 'client_admin', label: 'Client Admin', description: 'Manage their organization only' },
    { value: 'location_manager', label: 'Location Manager', description: 'Manage specific location' },
    { value: 'employee', label: 'Employee', description: 'View own stats only' }
];

const API_BASE = window.location.origin;

// v4.10.0 — Per-organization onboarding explainer URL (Loom / YouTube / Vimeo).
// Lightweight, optional. Used by SetupGuidanceCard + MyTap invite email.
function OnboardingExplainerSetup() {
    const { t } = useTranslation();
    const [url, setUrl] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/setup-guide/config')
            .then((r) => setUrl(r?.data?.onboarding_explainer_url || ''))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/setup-guide/config', {
                onboarding_explainer_url: url.trim() || null,
            });
            refreshExplainerCache();
            toast.success(t('setup_guide.explainer.saved'));
        } catch (err) {
            const detail = err?.response?.data?.detail || '';
            if (detail === 'explainer_host_not_allowed' || (typeof detail === 'string' && detail.includes('host_not_allowed'))) {
                toast.error(t('setup_guide.explainer.host_not_allowed'));
            } else {
                toast.error(t('setup_guide.explainer.save_failed'));
            }
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return null;

    return (
        <div data-testid="onboarding-explainer-setup" className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('setup_guide.explainer.description')}</p>
            <div className="flex flex-col sm:flex-row gap-2">
                <Input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.loom.com/share/..."
                    data-testid="onboarding-explainer-input"
                    data-insight-id="admin.workflow.onboarding-explainer"
                    data-insight-label="Operator first-impression video"
                    className="flex-1"
                />
                <Button onClick={save} disabled={saving} data-testid="onboarding-explainer-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                </Button>
            </div>
            {url && (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:underline"
                    data-testid="onboarding-explainer-preview"
                >
                    <PlayCircle size={13} strokeWidth={1.75} />
                    {t('setup_guide.explainer.preview')}
                </a>
            )}
            <p className="text-xs text-muted-foreground">{t('setup_guide.explainer.allowed_hosts')}</p>
        </div>
    );
}

function StaffPinSetup() {
    const { t } = useTranslation();
    const [pin, setPin] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [hasPin, setHasPin] = useState(null);
    const token = localStorage.getItem('yulontap_token');

    useEffect(() => {
        fetch(`${API_BASE}/api/vouchers/has-pin`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => setHasPin(d.has_pin)).catch(() => {});
    }, [token]);

    const save = async () => {
        if (pin.length !== 4 || pin !== confirm) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/setup-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ pin }),
            });
            if (res.ok) {
                toast.success('Staff PIN set successfully');
                setHasPin(true);
                setPin('');
                setConfirm('');
            } else {
                toast.error('Failed to set PIN');
            }
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                {hasPin ? t('adminSettings.pinHintSet') : t('adminSettings.pinHintEmpty')}
            </p>
            <div className="flex gap-2 items-end flex-wrap">
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('adminSettings.newPin')}</label>
                    <Input
                        data-testid="setup-pin-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="____"
                        className="w-28 text-center tracking-widest"
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">{t('adminSettings.confirm')}</label>
                    <Input
                        data-testid="setup-pin-confirm"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="____"
                        className="w-28 text-center tracking-widest"
                    />
                </div>
                <Button
                    data-testid="setup-pin-save"
                    size="sm"
                    onClick={save}
                    disabled={pin.length !== 4 || pin !== confirm || saving}
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : hasPin ? t('adminSettings.updatePin') : t('adminSettings.setPin')}
                </Button>
            </div>
            {pin.length === 4 && confirm.length === 4 && pin !== confirm && (
                <p className="text-xs text-red-400">{t('adminSettings.pinsDontMatch')}</p>
            )}
        </div>
    );
}

const AdminSettings = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isRepairing, setIsRepairing] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        password: '',
        role: 'client_admin',
        organization_id: '',
        location_id: ''
    });
    
    const { isSuperAdmin, user, logout } = useAuth();

    useEffect(() => {
        if (!isSuperAdmin()) {
            toast.error('Access denied');
            return;
        }
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await adminAPI.listUsers();
            setUsers(response.data);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleRepairTapData = async () => {
        setIsRepairing(true);
        try {
            const response = await adminAPI.repairTapEmployees();
            toast.success(response.data.message);
        } catch (error) {
            toast.error('Failed to repair tap data');
        } finally {
            setIsRepairing(false);
        }
    };
    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setNewUser({ ...newUser, password });
        setGeneratedPassword(password);
    };

    const handleCreateUser = async () => {
        if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            await authAPI.register(newUser);
            toast.success('User created successfully');
            setIsUserModalOpen(false);
            setNewUser({
                name: '',
                email: '',
                password: '',
                role: 'client_admin',
                organization_id: '',
                location_id: ''
            });
            setGeneratedPassword('');
            loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (userId === user?.id) {
            toast.error('Cannot delete your own account');
            return;
        }
        if (!window.confirm('Are you sure you want to deactivate this user?')) {
            return;
        }

        try {
            await adminAPI.deleteUser(userId);
            toast.success('User deactivated');
            loadUsers();
        } catch (error) {
            toast.error('Failed to deactivate user');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getRoleBadge = (role) => {
        const colors = {
            super_admin: 'bg-purple-100 text-purple-700',
            client_admin: 'bg-blue-100 text-blue-700',
            location_manager: 'bg-green-100 text-green-700',
            employee: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
        };
        return (
            <Badge className={colors[role] || 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}>
                {ROLES.find(r => r.value === role)?.label || role}
            </Badge>
        );
    };

    if (!isSuperAdmin()) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground">Access denied. Super Admin only.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-20 rounded-xl" />
                <div className="skeleton h-96 rounded-xl" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Admin Settings
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('adminSettings.subtitle')}</p>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-hover cursor-pointer" onClick={() => setIsUserModalOpen(true)} data-insight-id="admin.workflow.create-user" data-insight-label="Create new operator account">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Plus className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-foreground">{t('adminSettings.createUser')}</p>
                            <p className="text-sm text-muted-foreground">{t('adminSettings.createUserHint')}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-hover">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <Users className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-foreground">{t('adminSettings.totalUsers')}</p>
                            <p className="text-2xl font-bold text-foreground">{users.length}</p>
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-hover">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                            <Shield className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-foreground">{t('adminSettings.superAdmins')}</p>
                            <p className="text-2xl font-bold text-foreground">
                                {users.filter(u => u.role === 'super_admin').length}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card 
                    className="card-hover cursor-pointer border-blue-200 dark:border-blue-800 hover:border-blue-300" 
                    onClick={handleRepairTapData}
                    data-testid="repair-tap-data-card"
                    data-insight-id="admin.workflow.repair-tap-data"
                    data-insight-label="Repair tap attribution history"
                >
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            {isRepairing ? (
                                <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                            ) : (
                                <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            )}
                        </div>
                        <div>
                            <p className="font-semibold text-blue-700 dark:text-blue-300">
                                {isRepairing ? 'Repairing...' : 'Repair Tap Data'}
                            </p>
                            <p className="text-sm text-muted-foreground">Fix employee attribution on past tap records</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Users Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Users className="w-5 h-5" />
                        All Users
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(() => {
                                    // Tag the first row whose delete button actually renders
                                    // (the current operator's own row hides the delete button).
                                    const firstDeletableIdx = users.findIndex(u => u.id !== user?.id);
                                    return users.map((u, idx) => (
                                    <TableRow key={u.id}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                                                    <span className="text-sm font-medium text-muted-foreground">
                                                        {u.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <span className="font-medium">{u.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                                        <TableCell>
                                            <Badge className={u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                                {u.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {parseUtc(u.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {u.id !== user?.id && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => handleDeleteUser(u.id)}
                                                    data-insight-id={idx === firstDeletableIdx ? "admin.workflow.delete-user" : undefined}
                                                    data-insight-label={idx === firstDeletableIdx ? "Delete operator account" : undefined}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    ));
                                })()}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Tap Endpoint Info */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Key className="w-5 h-5" />
                        Tap Engine Card Endpoint
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                        Use this endpoint for your Tap Engine Cards to redirect customers:
                    </p>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg font-mono text-sm">
                        <code className="flex-1 text-foreground">
                            {window.location.origin}/api/tap?card=CARD_ID
                        </code>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`${window.location.origin}/api/tap?card=CARD_ID`)}
                        >
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Replace CARD_ID with the actual card ID (e.g., DEL001)
                    </p>
                </CardContent>
            </Card>

            {/* Create User Modal */}
            <Dialog open={isUserModalOpen} onOpenChange={setIsUserModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Create New User
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="mb-2 block">Full Name *</Label>
                            <Input
                                value={newUser.name}
                                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                                placeholder={t('adminUsers.placeholderName')}
                                data-testid="new-user-name"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">Email *</Label>
                            <Input
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                placeholder="john@example.com"
                                data-testid="new-user-email"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">Password *</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    placeholder="Enter password"
                                    data-testid="new-user-password"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={generatePassword}
                                >
                                    Generate
                                </Button>
                            </div>
                            {generatedPassword && (
                                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                    <Copy 
                                        className="w-3 h-3 cursor-pointer" 
                                        onClick={() => copyToClipboard(generatedPassword)}
                                    />
                                    Click to copy: {generatedPassword}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="mb-2 block">Role *</Label>
                            <Select 
                                value={newUser.role} 
                                onValueChange={(val) => setNewUser({ ...newUser, role: val })}
                            >
                                <SelectTrigger data-testid="new-user-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map(role => (
                                        <SelectItem key={role.value} value={role.value}>
                                            <div>
                                                <p>{role.label}</p>
                                                <p className="text-xs text-muted-foreground">{role.description}</p>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsUserModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateUser} data-testid="create-user-btn">
                            Create User
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Staff PIN Setup */}
            <Card className="mt-6" data-testid="staff-pin-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Key className="w-5 h-5" />
                        Staff Voucher PIN
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <StaffPinSetup />
                </CardContent>
            </Card>

            {/* v4.10.0 — Onboarding Explainer Link (per-org, optional) */}
            <Card className="mt-6" data-testid="onboarding-explainer-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <PlayCircle className="w-5 h-5" />
                        <I18nText k="setup_guide.explainer.title" />
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <OnboardingExplainerSetup />
                </CardContent>
            </Card>
        </div>
    );
};

// Tiny inline i18n string component to avoid wiring useTranslation in the
// already-untyped parent component.
const I18nText = ({ k }) => {
    const { t } = useTranslation();
    return <>{t(k)}</>;
};

export default AdminSettings;
